import { MdPhone } from "react-icons/md";
import { BsEnvelopeFill, BsWhatsapp, BsBellFill } from "react-icons/bs";
import { Button } from "../../Components/UI/button";
import { Tooltip, TooltipTrigger, TooltipContent } from "../../Components/UI/tooltip";
import { cn } from "../../Lib/Cn";
import WspDialog from "./WspDialog";
import { useState } from "react";
import { useDesktop } from "./IsDesktop";
import { toast } from "sonner";
import useIsMobile from "../../Hooks/useIsMobile";
import { useSelector } from "react-redux";
import { handlePhoneClickAction, handleWhatsAppClickAction } from "../../Utils/ContactLogUtils";
import TemplateDialog from "./TemplateDialog";

const ContactActions = ({
  connectionData, showDashes = true, showEmail = true, showTemplate = false, type = "CONTACT", connID = null, onRecordAdded, stageID
}) => {
  const isDesktop = useDesktop();
  const phone = connectionData?.mobile || connectionData?.Mobile || connectionData?.MNo || connectionData?.phone;
  const cleanedPhone = phone?.replace(/\D/g, "");
  const isValidPhone = cleanedPhone != '' && cleanedPhone != null
  let rawEmail = connectionData?.email || connectionData?.Email || connectionData?.Eml || connectionData?.contactEmail || connectionData?.ContactEmail;
  if (!rawEmail && connectionData?.Contacts) {
    try {
      const parsedContacts = typeof connectionData.Contacts === 'string'
        ? JSON.parse(connectionData.Contacts)
        : connectionData.Contacts;
      if (Array.isArray(parsedContacts) && parsedContacts.length > 0) {
        const primaryContact = parsedContacts.find(c => c.isPrimary || c.IsPrimary) || parsedContacts[0];
        rawEmail = primaryContact?.Eml || primaryContact?.Email || primaryContact?.email;
      }
    } catch (e) { }
  }
  const email = rawEmail?.trim();
  const isValidEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const [isWspDialogOpen, setIsWspDialogOpen] = useState(false);
  const [buttonRef, setButtonRef] = useState(null);
  const [wspDialogType, setWspDialogType] = useState("contact");
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  showDashes = showDashes && (isValidEmail || isValidPhone);
  const isMobile = useIsMobile();

  const createEmailLink = (email) => `mailto:${email.trim()}`;

  const handlePhoneClick = async (e) => {
    e.stopPropagation();

    await handlePhoneClickAction(connectionData, isDesktop, onRecordAdded, connID);
  };

  let noResultDashSpan = <span className="text-text-color h-5 w-6 flex items-center justify-center leading-none mt-[2px]">-</span>;
  let commonBtnClass = "h-6 w-6 p-0 flex items-center justify-center rounded-md hover:shadow-sm transform-gpu transition-transform transition-colors duration-200 ease-out hover:scale-110 active:scale-95";
  let btnHoverClass = {
    phone: "hover:bg-sahi-blue-light",
    email: "hover:bg-sahi-orange-light",
    whatsapp: "hover:bg-sahi-green-light",
    bell: "hover:bg-sahi-blue-light"
  }
  let commonIconClass = "h-4 w-4 text-black";
  let btnIconClass = {
    phone: "text-black",
    email: "text-sahi-orange",
    whatsapp: "text-sahi-green",
    bell: "text-sahi-blue",
  }
  const tooltipContentClass = "z-[9999] max-w-[250px] break-all whitespace-normal bg-slate-900 text-white p-2 rounded shadow-md";
  return (
    <>
      <div className={`flex items-center ${isMobile ? 'gap-4' : 'gap-2'} xl:gap-2.5`}>

        {/* Notify/Template Dialog Button - Only if showTemplate is true */}
        {isValidPhone && showTemplate && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                ref={setButtonRef}
                variant="ghost"
                size="sm"
                className={cn(commonBtnClass, btnHoverClass.bell)}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click
                  setIsWspDialogOpen(true);
                }}
              >
                <BsBellFill className={cn(commonIconClass, btnIconClass.bell)} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-center">Send<br />Message</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Phone */}
        {isValidPhone && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(commonBtnClass, btnHoverClass.phone)}
                onClick={handlePhoneClick}
              >
                <MdPhone className={cn(commonIconClass, btnIconClass.phone)} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className={tooltipContentClass}>
              {phone}
            </TooltipContent>
          </Tooltip>
        )}
        {/* Show dash when showDashes is true and phone is not valid */}
        {!isValidPhone && showDashes && <span className="text-text-color h-5 w-6 flex items-center justify-center leading-none mt-[2px]">-</span>}

        {/* Email */}
        {showEmail && isValidEmail && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(commonBtnClass, btnHoverClass.email)}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click
                  window.open(createEmailLink(email));
                }}
              >
                <BsEnvelopeFill className={cn(commonIconClass, btnIconClass.email)} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="end" // This moves the tooltip to the left
              sideOffset={5}
              className="z-[9999] max-w-[300px] break-all whitespace-normal bg-slate-900 text-white p-2 rounded shadow-md"
            >
              {email}
            </TooltipContent>
          </Tooltip>
        )}
        {/* Show dash when showDashes is true and email is not valid */}
        {showEmail && !isValidEmail && showDashes && <span className="text-text-color h-5 w-6 flex items-center justify-center leading-none mt-[2px]">-</span>}

        {/* WhatsApp - Direct Link or Dialog based on type */}
        {isValidPhone && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(commonBtnClass, btnHoverClass.whatsapp)}
                onClick={async (e) => {
                  e.stopPropagation(); // Prevent row click
                  if (type === "MIS") {
                    setWspDialogType("MIS");
                    setIsWspDialogOpen(true);
                  } else {
                    e.stopPropagation();
                    setTemplateDialogOpen(true);
                  }
                }}
              >
                <BsWhatsapp className={cn(commonIconClass, btnIconClass.whatsapp)} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="end" // This moves the tooltip to the left
              sideOffset={5}
              className="z-[9999] max-w-[300px] break-all whitespace-normal bg-slate-900 text-white p-2 rounded shadow-md"
            >
              <p>{phone}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Show dash when showDashes is true and phone is not valid */}
        {!isValidPhone && showDashes && <span className="text-text-color h-5 w-6 flex items-center justify-center leading-none mt-[2px]">-</span>}
        {!isValidPhone && showDashes && showTemplate && <span className="text-text-color h-5 w-6 flex items-center justify-center leading-none mt-[2px]">-</span>}
      </div>

      {/* WhatsApp Dialog */}
      {(showTemplate || type === "MIS") && isWspDialogOpen && (
        <WspDialog
          isOpen={isWspDialogOpen}
          onClose={() => setIsWspDialogOpen(false)}
          connectionData={connectionData}
          phone={phone}
          anchorEl={buttonRef}
          type={wspDialogType}
        />
      )}

      <TemplateDialog
        isOpen={templateDialogOpen}
        onClose={() => { setTemplateDialogOpen(false) }}
        connectionData={connectionData}
        onRecordAdded={onRecordAdded}
        connID={connID}
        stageID={stageID || connectionData?.currStageID || connectionData?.stageID}
      />
    </>
  );
};

export default ContactActions;